import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../../lib/api';
import { selectActiveOrg, useAuthStore } from '../auth/store';
import { addDaysIso, mondayOf, todayIso } from './hours';

/** Monday after this one — the exclusive upper bound for "recent" rows. */
function nextMondayIso(): string {
  return addDaysIso(mondayOf(todayIso()), 7);
}

// ---------------------------------------------------------------------------
// Types (mirror backend/src/modules/time responses)
// ---------------------------------------------------------------------------

export interface TimePerson {
  id: string;
  name: string;
  email: string;
  type: 'STAFF' | 'CONTRACTOR';
  isActive: boolean;
  /**
   * Contracted hours per week. The timesheet's target is THIS, never a
   * hardcoded 40 — see `capacity.ts`. null = no committed capacity.
   */
  weeklyCapacityHours: number | null;
}

export interface TimeTask {
  id: string;
  name: string;
}

/** Task option offered by an agreement (from AgreementTask, with resolved defaults). */
export interface TimeTaskOption extends TimeTask {
  billable: boolean;
}

export interface TimeAgreement {
  id: string;
  name: string;
  code: string | null;
  type: string;
  billBy: 'HOURS' | 'FIXED' | 'NONE';
  isActive: boolean;
  client: { id: string; name: string };
  /** Present on /time/agreements responses (add-row picker cascade). */
  tasks?: TimeTaskOption[];
}

export interface TimeEntry {
  id: string;
  date: string;
  hours: number;
  billable: boolean;
  billRate: number | null;
  notes: string | null;
  locked: boolean;
  invoiceId: string | null;
  taskId: string | null;
  task: TimeTask | null;
  /**
   * Deleted in Harvest while sync was running. The row is kept (it may already
   * be on an issued invoice) but it is not work any more: struck through here,
   * refused by every write path, and never offered up for invoicing.
   */
  deletedInSource: boolean;
  deletedInSourceAt: string | null;
  /** Harvest's own lock — approved timesheet, closed period, billed there. */
  lockedInSource: boolean;
  lockedReason: string | null;
  /**
   * ⚠ SOMEBODY ELSE'S HANDWRITING. The last person to write these hours, when
   * that was NOT their owner — an approver fixing a colleague's week. null on
   * every ordinary entry, so the grid marks the exception and stays quiet
   * about the rule.
   */
  editedBy: { personId: string; name: string; at: string | null } | null;
  /**
   * A timer is running on this entry. Its elapsed time is nowhere but
   * `runningSince` on the server, so a cell write treats it like a billed hour
   * — a floor, never rewritten, never deleted to make room.
   */
  running: boolean;
}

/**
 * One recorded write to somebody's hours by somebody else.
 *
 * Rides along inside `/time/week` and `/time/day` rather than living behind a
 * click: the whole point is that the owner is TOLD. The sentence was composed
 * on the server at the moment of the write and is never re-derived.
 */
export interface TimeChange {
  id: string;
  action: 'added' | 'changed' | 'removed' | string;
  summary: string;
  actorName: string | null;
  actorPersonId: string | null;
  date: string;
  hoursBefore: number | null;
  hoursAfter: number | null;
  at: string;
}

export interface WeekRow {
  agreementId: string;
  taskId: string | null;
  agreement: TimeAgreement;
  task: TimeTask | null;
  billable: boolean;
  entries: TimeEntry[];
  dayHours: Record<string, number>;
  total: number;
}

// ---------------------------------------------------------------------------
// Week submission & approval
// ---------------------------------------------------------------------------

export type TimesheetStatus = 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED';

export interface TimesheetPerson {
  id: string;
  name: string;
}

/** What the signed-in viewer may do — decided on the SERVER, never inferred. */
export interface TimesheetAbilities {
  submit: boolean;
  unsubmit: boolean;
  approve: boolean;
  reject: boolean;
  reopen: boolean;
}

/**
 * The review state of one person's week.
 *
 * `locked` is the REVIEW lock (submitted/approved), which is a different thing
 * from `TimeEntry.locked` — that one is the BILLING lock (this hour is on an
 * issued invoice). The two are independent and the UI must never merge them
 * into one grey "locked" glyph.
 */
/** How often a person submits: the period a timesheet covers, or never. */
export type TimesheetCadence = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'NEVER';
/** The span one timesheet covers. */
export type PeriodKind = 'DAY' | 'WEEK' | 'MONTH';

/**
 * One timesheet — a PERIOD (a day, a week or a month, per the person's
 * `timesheetCadence`), not always a week. The screens still VIEW a week; what
 * is submitted, locked and approved is the person's period.
 */
export interface Timesheet {
  /** The stored row, or null for a period nobody has submitted (DRAFT). */
  id: string | null;
  periodKind: PeriodKind;
  periodStart: string;
  periodEnd: string;
  /** "Aug 10 – 16, 2026" / "August 2026" / "Tue, Sep 29, 2026" — print this. */
  label: string;
  /** @deprecated — equals `periodStart`. */
  weekStart: string;
  person: TimesheetPerson;
  status: TimesheetStatus;
  submittedAt: string | null;
  reviewedAt: string | null;
  reviewedBy: TimesheetPerson | null;
  /** The OWNER's note, written when they submitted. */
  note: string | null;
  /** The REVIEWER's note — the rejection reason. */
  reviewNote: string | null;
  locked: boolean;
  isOwn: boolean;
  /** @deprecated — `isOwn`. */
  isOwnWeek: boolean;
  /** Is a submission of this period expected (it is the person's cadence)? */
  expected: boolean;
  /** First day it may be submitted — the period's last workday. */
  submittableFrom: string;
  can: TimesheetAbilities;
}

/** The period governing one day of a viewed range. */
export interface TimesheetDay {
  status: TimesheetStatus;
  locked: boolean;
  periodKind: PeriodKind;
  periodStart: string;
  label: string;
}

/** Submission state for a viewed range — rides on /time/week and /time/day. */
export interface TimesheetRange {
  /** The period governing the range's FIRST day. */
  timesheet: Timesheet;
  /** Every period touching the range — two months when a week crosses one. */
  periods: Timesheet[];
  /** Per day (YYYY-MM-DD): the governing period and whether it takes writes. */
  dayStatus: Record<string, TimesheetDay>;
  /** The person's cadence. */
  cadence: TimesheetCadence;
}

export interface WeekData extends TimesheetRange {
  person: TimePerson;
  weekStart: string;
  days: string[];
  rows: WeekRow[];
  dayTotals: Record<string, number>;
  weekTotal: number;
  /** Arrives with the hours, so the pill can never disagree with the cells. */
  timesheet: Timesheet;
  /** Writes to this week made by somebody other than its owner, newest first. */
  changes: TimeChange[];
}

export interface DayEntry extends TimeEntry {
  agreement: TimeAgreement;
}

export interface DayData extends TimesheetRange {
  person: TimePerson;
  date: string;
  entries: DayEntry[];
  total: number;
  /** The review state of the WEEK this day belongs to. */
  timesheet: Timesheet;
  /** Writes to this day made by somebody other than its owner, newest first. */
  changes: TimeChange[];
}

export interface RunningTimer {
  personId: string;
  agreementId: string;
  taskId: string | null;
  agreement: TimeAgreement;
  task: TimeTask | null;
  notes: string | null;
  startedAt: string;
  /** The day the clock's hours land on (YYYY-MM-DD). */
  date: string;
  entryId: string;
  baseHours: number;
}

// ---------------------------------------------------------------------------
// Query hooks — all keys start with ['time', orgId] so org switches refetch.
// ---------------------------------------------------------------------------

export function useActiveOrgId(): string | undefined {
  return useAuthStore((s) => selectActiveOrg(s)?.id);
}

/**
 * A timesheet that cannot load must SAY so quickly.
 *
 * The app-wide default (three attempts, exponential backoff) left this screen
 * sitting on a convincing skeleton for ten seconds before admitting anything
 * was wrong — the worst possible answer for someone who is about to type their
 * day into it. One quick retry covers a blip; anything worse gets the error
 * card. Client errors are never retried at all.
 */
const failFast = {
  retry: (failureCount: number, error: unknown) => {
    if (error instanceof ApiError && error.status < 500) return false;
    return failureCount < 1;
  },
  retryDelay: 400,
};

/** Who the viewer is inside this org, and whether they may review weeks. */
export interface TimeViewer {
  personId: string | null;
  canApprove: boolean;
  isAdmin: boolean;
}

export function usePeople() {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: ['time', orgId, 'people'],
    queryFn: () =>
      api.get<{ people: TimePerson[]; defaultPersonId: string | null; viewer: TimeViewer }>(
        '/time/people',
      ),
    enabled: Boolean(orgId),
    ...failFast,
  });
}

export function useAgreements() {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: ['time', orgId, 'agreements'],
    queryFn: () => api.get<{ agreements: TimeAgreement[] }>('/time/agreements'),
    enabled: Boolean(orgId),
    ...failFast,
  });
}

export function useWeek(weekStart: string, personId: string | null) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: ['time', orgId, 'week', weekStart, personId],
    queryFn: () =>
      api.get<WeekData>('/time/week', {
        params: { start: weekStart, personId: personId ?? undefined },
      }),
    enabled: Boolean(orgId),
    ...failFast,
  });
}

export function useDay(date: string, personId: string | null) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: ['time', orgId, 'day', date, personId],
    queryFn: () =>
      api.get<DayData>('/time/day', { params: { date, personId: personId ?? undefined } }),
    enabled: Boolean(orgId),
    ...failFast,
  });
}

export function useTimer(personId: string | null) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: ['time', orgId, 'timer', personId],
    queryFn: () =>
      api.get<{ timer: RunningTimer | null }>('/time/timer/current', {
        params: { personId: personId ?? undefined },
      }),
    enabled: Boolean(orgId),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

// ---------------------------------------------------------------------------
// Mutations — mutationKey ['time-save'] powers the saved/saving indicator.
// ---------------------------------------------------------------------------

function useInvalidateTime() {
  const queryClient = useQueryClient();
  const orgId = useActiveOrgId();
  return () => queryClient.invalidateQueries({ queryKey: ['time', orgId] });
}

/** One entry an undo puts back, exactly as it was. */
export interface RestoreEntry {
  /** The id it had before it was removed — reused when it is free. */
  id?: string | null;
  hours: number;
  notes?: string | null;
  billable?: boolean;
}

/** An entry a write DELETED — what the undo bar names. */
export interface RemovedEntry {
  id: string;
  hours: number;
  notes: string | null;
  billable: boolean;
}

export interface SetCellInput {
  personId?: string;
  agreementId: string;
  taskId: string | null;
  date: string;
  /** The absolute day total. Ignored — and omitted — when `restore` is given. */
  hours?: number;
  /** Optional: written onto the entry the call creates/updates (in-place note). */
  notes?: string | null;
  billable?: boolean;
  /**
   * ⚠ **THE ONLY HONEST UNDO OF A CELL WRITE.**
   *
   * Replaying a day TOTAL cannot put a note, a billable flag or a third entry
   * back: a cell of 0.50 “Alpha” / 2.00 “Beta” / 1.00 “Gamma” written down to
   * 2.00 and back up to 3.50 reconciles to the penny with two authored notes
   * destroyed. So the undo sends the ROWS and the server reinstates exactly
   * them, ids and all.
   */
  restore?: RestoreEntry[];
  /**
   * ⚠ **THE ROWS THAT WRITE LEFT BEHIND — what makes the undo reconcilable.**
   *
   * A snapshot describes a moment, and the cell moves on: the popover adds a
   * fourth entry, the day view edits one, an approver rewrites the cell in
   * another tab. Replaying the snapshot over that hard-DELETED the newer work
   * — silently, and with the change popped so there was no second undo.
   *
   * With `expect` the server keeps every row the undo did not put there, and
   * REFUSES outright when a row it would rewrite has since been edited or
   * deleted. See `CellWriteInput.expect` in the time module.
   */
  expect?: RestoreEntry[];
}

export interface SetCellResult {
  /** The entry this call created or updated (null when the cell was cleared). */
  updatedEntryId: string | null;
  entries: TimeEntry[];
  total: number;
  /** Rows this write deleted, with their notes — never just a total. */
  removed: RemovedEntry[];
  /**
   * Rows an undo left standing because they were logged AFTER the change it
   * reverted. Always printed: an undo that covers less than it claims is the
   * defect this field exists to make impossible to hide.
   */
  kept: RemovedEntry[];
}

/**
 * The unlocked rows a write LEFT in a cell — the shape `expect` takes.
 *
 * Billed hours, Harvest-deleted hours and a running clock are never rewritten
 * by a cell write, so they are never part of undoing one either.
 */
export function writtenRows(entries: TimeEntry[]): RestoreEntry[] {
  return entries
    .filter((e) => !e.locked && !e.deletedInSource && !e.running)
    .map((e) => ({ id: e.id, hours: e.hours, notes: e.notes, billable: e.billable }));
}

/** The day total a cell write is asking for, whichever shape it came in. */
export function cellTotalOf(input: { hours?: number; restore?: RestoreEntry[] }): number {
  if (input.restore) {
    return Math.round(input.restore.reduce((sum, row) => sum + row.hours, 0) * 100) / 100;
  }
  return input.hours ?? 0;
}

/** Set a week-grid cell to an absolute total, with an optimistic cache patch. */
export function useSetCell(weekStart: string, personId: string | null) {
  const queryClient = useQueryClient();
  const orgId = useActiveOrgId();
  const weekKey = ['time', orgId, 'week', weekStart, personId];
  return useMutation({
    mutationKey: ['time-save'],
    mutationFn: (input: SetCellInput) =>
      api.post<SetCellResult>('/time/cell', {
        ...input,
        personId: personId ?? undefined,
      }),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: weekKey });
      const previous = queryClient.getQueryData<WeekData>(weekKey);
      if (previous) {
        const asked = cellTotalOf(input);
        const rows = previous.rows.map((row) =>
          row.agreementId === input.agreementId && (row.taskId ?? null) === (input.taskId ?? null)
            ? {
                ...row,
                dayHours: { ...row.dayHours, [input.date]: asked },
                total:
                  Math.round((row.total - (row.dayHours[input.date] ?? 0) + asked) * 100) / 100,
              }
            : row,
        );
        const dayTotals = { ...previous.dayTotals };
        dayTotals[input.date] =
          Math.round(rows.reduce((s, r) => s + (r.dayHours[input.date] ?? 0), 0) * 100) / 100;
        const weekTotal = Math.round(rows.reduce((s, r) => s + r.total, 0) * 100) / 100;
        queryClient.setQueryData<WeekData>(weekKey, { ...previous, rows, dayTotals, weekTotal });
      }
      return { previous };
    },
    onError: (_err, _input, context) => {
      if (context?.previous) queryClient.setQueryData(weekKey, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['time', orgId] }),
  });
}

export interface SetCellsInput {
  personId?: string;
  cells: Array<Omit<SetCellInput, 'personId'>>;
}

export interface SetCellsResult {
  cells: Array<SetCellResult & { agreementId: string; taskId: string | null; date: string }>;
  count: number;
}

/**
 * ⚠ **SEVERAL CELLS, ONE CALL, ONE TRANSACTION.**
 *
 * `F` (fill the rest of the week) and Ctrl+Z each move a SET of cells from one
 * keystroke. Looping `useSetCell` over them made each cell its own request, so
 * a fill could half-land and an undo could put ONE cell back and strand the
 * rest — 32 h of hours nobody authored, still billable. `POST /time/cells`
 * writes every cell or none of them, and the grid never issues the loop.
 */
export function useSetCells(weekStart: string, personId: string | null) {
  const queryClient = useQueryClient();
  const orgId = useActiveOrgId();
  const weekKey = ['time', orgId, 'week', weekStart, personId];
  return useMutation({
    mutationKey: ['time-save'],
    mutationFn: (input: SetCellsInput) =>
      api.post<SetCellsResult>('/time/cells', {
        cells: input.cells,
        personId: personId ?? undefined,
      }),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: weekKey });
      const previous = queryClient.getQueryData<WeekData>(weekKey);
      if (previous) {
        // Same optimistic patch as one cell, applied to every cell in the
        // batch — so the grid shows the whole write landing at once, exactly
        // as the server will do it.
        const byRow = new Map<string, Map<string, number>>();
        for (const cell of input.cells) {
          const key = `${cell.agreementId}|${cell.taskId ?? ''}`;
          const days = byRow.get(key) ?? new Map<string, number>();
          days.set(cell.date, cellTotalOf(cell));
          byRow.set(key, days);
        }
        const rows = previous.rows.map((row) => {
          const days = byRow.get(`${row.agreementId}|${row.taskId ?? ''}`);
          if (!days) return row;
          const dayHours = { ...row.dayHours };
          for (const [date, hours] of days) dayHours[date] = hours;
          const total =
            Math.round(Object.values(dayHours).reduce((s, h) => s + h, 0) * 100) / 100;
          return { ...row, dayHours, total };
        });
        const dayTotals = { ...previous.dayTotals };
        for (const day of previous.days) {
          dayTotals[day] =
            Math.round(rows.reduce((s, r) => s + (r.dayHours[day] ?? 0), 0) * 100) / 100;
        }
        const weekTotal = Math.round(rows.reduce((s, r) => s + r.total, 0) * 100) / 100;
        queryClient.setQueryData<WeekData>(weekKey, { ...previous, rows, dayTotals, weekTotal });
      }
      return { previous };
    },
    onError: (_err, _input, context) => {
      if (context?.previous) queryClient.setQueryData(weekKey, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['time', orgId] }),
  });
}

export interface CreateEntryInput {
  personId?: string;
  agreementId: string;
  taskId?: string | null;
  date: string;
  hours: number;
  notes?: string;
  billable?: boolean;
}

export function useCreateEntry() {
  const invalidate = useInvalidateTime();
  return useMutation({
    mutationKey: ['time-save'],
    mutationFn: (input: CreateEntryInput) => api.post<{ entry: DayEntry }>('/time', input),
    onSettled: invalidate,
  });
}

export function useUpdateEntry() {
  const invalidate = useInvalidateTime();
  return useMutation({
    mutationKey: ['time-save'],
    mutationFn: ({
      id,
      ...input
    }: {
      id: string;
      hours?: number;
      notes?: string | null;
      billable?: boolean;
      taskId?: string | null;
      /** Move the entry to another day (YYYY-MM-DD). */
      date?: string;
      /**
       * Move the entry to another agreement (wrong-project fix). The task is
       * kept when the new agreement offers it; the bill rate is re-resolved.
       */
      agreementId?: string;
    }) => api.patch<{ entry: DayEntry }>(`/time/${id}`, input),
    onSettled: invalidate,
  });
}

export function useDeleteEntry() {
  const invalidate = useInvalidateTime();
  return useMutation({
    mutationKey: ['time-save'],
    mutationFn: (id: string) => api.delete<{ deleted: boolean }>(`/time/${id}`),
    onSettled: invalidate,
  });
}

export function useStartTimer() {
  const invalidate = useInvalidateTime();
  return useMutation({
    mutationFn: (input: {
      personId?: string;
      agreementId: string;
      taskId?: string | null;
      notes?: string;
      /** Local calendar date the timer's entry logs to. */
      date?: string;
    }) => api.post<{ timer: RunningTimer | null }>('/time/timer/start', input),
    onSettled: invalidate,
  });
}

export function useStopTimer() {
  const invalidate = useInvalidateTime();
  return useMutation({
    mutationFn: (input: { personId?: string }) =>
      api.post<{ entry: TimeEntry; elapsedHours: number }>('/time/timer/stop', input),
    onSettled: invalidate,
  });
}

export function useDiscardTimer() {
  const invalidate = useInvalidateTime();
  return useMutation({
    mutationFn: (input: { personId?: string }) =>
      api.delete<{ discarded: boolean }>('/time/timer', {
        params: { personId: input.personId },
      }),
    onSettled: invalidate,
  });
}

export interface RecentRow {
  agreement: TimeAgreement;
  taskId: string | null;
  task: TimeTask | null;
}

export interface RecentRowsResponse {
  weekStart: string | null;
  /** Rows of the most recent non-empty timesheet — "copy last timesheet". */
  rows: RecentRow[];
  /**
   * Distinct combos used in the last 90 days, most-recently-used first. Wider
   * than `rows` on purpose: the pickers hide combos already on the open
   * timesheet, and `rows` usually IS that timesheet, which left the week
   * grid's "Recent" group empty exactly where it mattered most.
   */
  recent: RecentRow[];
}

/**
 * The person's most recently used agreement × task rows, from the SERVER.
 *
 * The picker's "Recent" list used to live only in localStorage, so a new device
 * (or a cleared browser) dropped a daily user back into an alphabetical list of
 * every agreement — even though the API already knows what they worked on last.
 * `before` is next Monday, so the current week counts as recent too.
 */
export function useRecentRowKeys(personId: string | null) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: ['time', orgId, 'recent-rows', personId],
    queryFn: () =>
      api.get<RecentRowsResponse>('/time/rows/recent', {
        params: { before: nextMondayIso(), personId: personId ?? undefined },
      }),
    enabled: Boolean(orgId),
    staleTime: 5 * 60_000,
  });
}

// ---------------------------------------------------------------------------
// Submission & approval
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// What needs you beyond the week on screen
// ---------------------------------------------------------------------------

export interface AttentionPeriod {
  periodKind: PeriodKind;
  periodStart: string;
  /** @deprecated — equals `periodStart`. */
  weekStart: string;
  label: string;
}

export interface AttentionRejectedWeek extends AttentionPeriod {
  reviewNote: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
}

export interface AttentionUnsubmittedWeek extends AttentionPeriod {
  hours: number;
}

export interface AttentionAwaitingWeek extends AttentionPeriod {
  count: number;
  people: string[];
}

/**
 * The three things /time could not see: a period sent back to you, periods you
 * tracked but never submitted (in YOUR cadence), and sheets waiting on your
 * approval.
 */
export interface TimeAttention {
  today: string;
  /** The viewer's own period kind, or null when nothing is expected of them. */
  periodKind: PeriodKind | null;
  /** How many of those periods back the strip looks. */
  lookback: number;
  rejected: AttentionRejectedWeek[];
  unsubmitted: {
    count: number;
    hours: number;
    periods: AttentionUnsubmittedWeek[];
    /** @deprecated — same list as `periods`. */
    weeks: AttentionUnsubmittedWeek[];
  };
  awaiting: {
    canApprove: boolean;
    count: number;
    periods: AttentionAwaitingWeek[];
    /** @deprecated — same list as `periods`. */
    weeks: AttentionAwaitingWeek[];
  };
  /** Nothing needs this viewer — the ordinary case, and nothing renders. */
  clear: boolean;
}

/**
 * ONE FETCH, TWO CONSUMERS: the strip at the top of /time and the count on the
 * Time nav item. Sharing this query key means the badge and the strip cannot
 * print different numbers, and the sidebar makes no request of its own.
 */
export function useTimeAttention() {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: ['time', orgId, 'attention'],
    queryFn: () => api.get<TimeAttention>('/time/attention'),
    enabled: Boolean(orgId),
    ...failFast,
  });
}

/** A person as the Approvals queue describes them. */
export interface ApprovalPerson {
  id: string;
  name: string;
  email: string;
  type: 'STAFF' | 'CONTRACTOR';
  cadence: TimesheetCadence;
  tracksTime: boolean;
  /** Submitting is required — off = optional, never late or reminded. */
  required: boolean;
  hasAccount: boolean;
  weeklyCapacityHours: number | null;
}

/** One period on the Approvals queue — a Timesheet plus what is in it. */
export interface ApprovalRow extends Omit<Timesheet, 'person'> {
  person: ApprovalPerson;
  hours: number;
  billableHours: number;
  /** Billable VALUE (hourly agreements only, work that still exists). */
  billableAmount: number;
  /** Billable hours on fee-based agreements — outside `billableAmount`. */
  feeWorkHours: number;
  daysTracked: number;
  /**
   * What the period COST us — hours × the cost rate (assignment, else the
   * person's), the P&L's rule. Null when the person is salaried.
   */
  cost: number | null;
  costBasis: 'hourly' | 'salary' | 'no-rate';
  /** Hours with no cost rate — left out of `cost`. */
  uncostedHours: number;
  /**
   * `missing` rows only: the last reminder about THIS period — by the
   * scheduler or a human — so every row states its own chase, not just the one
   * the next run would send. Absent on awaiting/recent rows.
   */
  remindedAt?: string | null;
  remindedOutcome?: string | null;
}

export interface ApprovalsQueue {
  today: string;
  viewer: { personId: string | null; isAdmin: boolean; canApprove: boolean };
  summary: {
    awaiting: number;
    awaitingHours: number;
    missing: number;
    missingNoTime: number;
    expectedPeople: number;
    notExpected: number;
  };
  /** SUBMITTED periods, any kind, oldest first. `can` says who may decide. */
  awaiting: ApprovalRow[];
  /** Closed periods expected people have not submitted (DRAFT or REJECTED). */
  missing: ApprovalRow[];
  /** Approved/rejected in the last 45 days, newest first. */
  recent: ApprovalRow[];
  /** Everybody active, with cadence and whether a submission is expected. */
  people: Array<ApprovalPerson & { expected: boolean }>;
}

/**
 * The approver's queue. Only fetched when the viewer can approve — the route is
 * 403 for everyone else.
 */
export function useApprovals(enabled: boolean) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: ['time', orgId, 'approvals'],
    queryFn: () => api.get<ApprovalsQueue>('/time/approvals'),
    enabled: Boolean(orgId) && enabled,
    ...failFast,
  });
}

export type ReviewCheckKind =
  | 'no-note'
  | 'long-day'
  | 'weekend'
  | 'logged-late'
  | 'edited'
  | 'duplicate'
  | 'timer-cap'
  | 'no-rate'
  | 'deleted'
  | 'inactive-agreement'
  | 'unusual-total';

/** One entry of a submitted period, with what it cost. */
export interface ReviewEntry {
  id: string;
  date: string;
  /** For the in-place correction an admin can make. */
  agreementId: string;
  taskId: string | null;
  client: string | null;
  agreement: string;
  task: string | null;
  notes: string | null;
  hours: number;
  billable: boolean;
  /** Cost per hour (assignment, else the person's); null = salaried or no rate. */
  rate: number | null;
  /** hours × rate, rounded per entry — the lines add up to the period. */
  amount: number | null;
  /** The day it was first written here; null when imported. */
  loggedOn: string | null;
  editedBy: { name: string; summary: string; at: string } | null;
  autoStopped: boolean;
  deletedInSource: boolean;
  invoiced: boolean;
  agreementInactive: boolean;
  flags: ReviewCheckKind[];
}

/** `GET /time/timesheets/:id/review` — one period, its own entries only. */
export interface TimesheetReview {
  timesheet: {
    id: string;
    status: TimesheetStatus;
    periodKind: PeriodKind;
    periodStart: string;
    periodEnd: string;
    label: string;
    submittedAt: string | null;
    note: string | null;
    reviewNote: string | null;
    person: { id: string; name: string; type: 'STAFF' | 'CONTRACTOR' };
  };
  totals: {
    hours: number;
    entries: number;
    days: number;
    cost: number | null;
    costBasis: 'hourly' | 'salary' | 'no-rate';
    uncostedHours: number;
  };
  usual: { hours: number; label: string; periodNoun: string } | null;
  checks: Array<{ kind: ReviewCheckKind; text: string; entryIds: string[] }>;
  entries: ReviewEntry[];
}

export function useTimesheetReview(timesheetId: string | null) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: ['time', orgId, 'review', timesheetId],
    queryFn: () => api.get<TimesheetReview>(`/time/timesheets/${timesheetId}/review`),
    enabled: Boolean(orgId) && Boolean(timesheetId),
    ...failFast,
  });
}

/**
 * Which period a transition is about: a stored row (`timesheetId`), an
 * explicit period (`periodKind` + `periodStart`), or `date` — the person's own
 * cadence period containing that day.
 */
export interface TimesheetActionInput {
  personId?: string;
  timesheetId?: string | null;
  periodKind?: PeriodKind;
  periodStart?: string;
  date?: string;
  note?: string | null;
}

export type TimesheetAction = 'submit' | 'unsubmit' | 'approve' | 'reject' | 'reopen';

/**
 * One hook for all five transitions.
 *
 * They differ only in their path and every one invalidates the same cache, so
 * five near-identical hooks would be five places for that invalidation to
 * drift — and a stale grid after an approval is a user typing into a week the
 * server has already locked.
 */
export function useTimesheetAction(action: TimesheetAction) {
  const invalidate = useInvalidateTime();
  return useMutation({
    mutationFn: (input: TimesheetActionInput) =>
      api.post<{ timesheet: Timesheet }>(`/time/timesheets/${action}`, input),
    onSettled: invalidate,
  });
}

/** Fetch (imperatively) the row descriptors of the most recent past week. */
export function fetchRecentRows(before: string, personId: string | null) {
  return api.get<RecentRowsResponse>('/time/rows/recent', {
    params: { before, personId: personId ?? undefined },
  });
}
